import { Check, Plus } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PATIENT_TAG_CATALOG } from "@/lib/patient-tags";
import { cn } from "@/lib/utils";

/**
 * "+ Tag" picker for the conversation header: toggles the 8 patient tags on the PATIENT
 * record (they ride along every conversation that patient has). Tags describe — they never
 * compete with workflow status.
 * @param {object} props
 * @param {string[]} props.activeTags — currently set tag keys
 * @param {(tagKey: string, checked: boolean) => void} props.onToggle
 */
export function PatientTagPicker({ activeTags = [], onToggle }) {
  const activeSet = new Set(activeTags);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Add or remove patient tags"
          className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-muted-foreground/40 px-1.5 py-[2px] text-[10.5px] font-medium leading-none text-muted-foreground transition hover:border-foreground/40 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} aria-hidden />
          Tag
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1.5">
        <div className="px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Patient tags
        </div>
        <ScrollArea viewportClassName="max-h-72">
          {PATIENT_TAG_CATALOG.map((def) => {
            const Icon = def.icon;
            const active = activeSet.has(def.key);
            return (
              <button
                key={def.key}
                type="button"
                role="switch"
                aria-checked={active}
                onClick={() => onToggle?.(def.key, !active)}
                className={cn(
                  "flex w-full items-start gap-2 rounded-md px-1.5 py-1.5 text-left text-xs transition hover:bg-muted/70",
                  active ? "bg-muted" : "",
                )}
              >
                <Icon
                  className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", def.glyphClassName)}
                  strokeWidth={2.25}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate font-medium text-foreground">{def.label}</span>
                  <span className="block truncate text-[10px] text-muted-foreground">
                    {def.description}
                  </span>
                </span>
                {active ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" strokeWidth={2.5} /> : null}
              </button>
            );
          })}
        </ScrollArea>
        <p className="border-t border-border/70 px-1.5 pb-0.5 pt-1.5 text-[10px] leading-snug text-muted-foreground">
          Set once on the patient — tags ride along every conversation.
        </p>
      </PopoverContent>
    </Popover>
  );
}
